import { DataVersion } from '@hakimo-ui/hakimo/types';
import { useLocationAlarmUpdates } from '@hakimo-ui/hakimo/data-access';
import { QueryResult, Timeline } from '@hakimo-ui/hakimo/ui-elements';
import { useExportCsv, useIsSupportRole } from '@hakimo-ui/hakimo/util';
import { Alert, Button } from '@hakimo-ui/shared/ui-base';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { getFormattedExportData } from './extraDetailsUtil';
import { getLocationAlarmUpdateEvents } from './util';

const REFETCH_INTERVAL = 60000;

export interface Props {
  locationAlarmId: string;
  locationName: string;
  timestamp: string;
  // Comes from the caller (ultimately the ?version=v3 URL param), not from
  // dataVersionOverrideAtom: this renders on the public shared-alarm route
  // where the Location Alarms v2/v3 toggle has no bearing on the alarm shown.
  dataVersion?: DataVersion;
}

export function AlarmUpdates(props: Props) {
  const { locationAlarmId, locationName, timestamp, dataVersion } = props;
  const query = useLocationAlarmUpdates(
    locationAlarmId,
    REFETCH_INTERVAL,
    timestamp,
    'remote-guarding',
    undefined,
    true,
    dataVersion
  );
  const isSupportRole = useIsSupportRole();
  const { exportCsv } = useExportCsv();
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = (events: any[]) => {
    setIsExporting(true);
    try {
      if (!events.length || !query.data?.items) {
        throw new Error('No data to export');
      }

      const formattedData = getFormattedExportData(events, query.data);
      if (!Array.isArray(formattedData) || formattedData.length === 0) {
        throw new Error('Formatted data is invalid or empty');
      }
      exportCsv({
        formattedData,
        fileName: `alarm-updates-${locationAlarmId}-${locationName}.csv`,
        fileType: 'csv',
      });
    } catch (error) {
      throw new Error(
        `Export Failed: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <h2 className="flex h-9 items-center pl-1 font-bold">Alarm Updates</h2>
        {isSupportRole && (
          <Button
            variant="icon"
            onClick={() => {
              if (!query.data) return;
              const events = getLocationAlarmUpdateEvents(
                query.data,
                isSupportRole,
                locationAlarmId,
                dataVersion
              );
              handleExport(events);
            }}
            loading={isExporting}
            title="Export Alarm Updates"
            disabled={!query.data}
          >
            <ArrowDownTrayIcon className="w-5" />
          </Button>
        )}
      </div>
      <QueryResult queryResult={query} loadingText="">
        {(data) => {
          const events = getLocationAlarmUpdateEvents(
            data,
            isSupportRole,
            locationAlarmId,
            dataVersion
          );
          return (
            <div className="dark:bg-ondark-bg-1 bg-onlight-bg-1 max-h-[30rem] overflow-auto rounded-lg py-2">
              {events.length > 0 ? (
                <Timeline events={events} />
              ) : (
                <div className="pt-4">
                  <Alert>No updates</Alert>
                </div>
              )}
            </div>
          );
        }}
      </QueryResult>
    </div>
  );
}

export default AlarmUpdates;
